import { normalizeLookSettings, pointerDeltaToRadians } from './settings.js';

const clamp = (value, min, max) => Math.min(max, Math.max(min, Number.isFinite(value) ? value : 0));
export const frameDelta = (delta) => clamp(delta, 0, 0.05);

export function stickIntent(x, y, radius = 52, deadZone = 0.16) {
  const distance = Math.hypot(x, y);
  const magnitude = Math.pow(
    clamp((distance / Math.max(1, radius) - deadZone) / (1 - deadZone), 0, 1),
    1.35,
  );
  return distance
    ? { x: (x / distance) * magnitude, y: (-y / distance) * magnitude }
    : { x: 0, y: 0 };
}

export function createInput() {
  return {
    keys: new Set(),
    stick: { x: 0, y: 0 },
    look: { x: 0, y: 0 },
    actions: new Set(),
    sprint: false,
    velocity: { x: 0, y: 0 },
  };
}

export function resetInput(input) {
  input.keys.clear();
  input.actions.clear();
  input.stick = { x: 0, y: 0 };
  input.look = { x: 0, y: 0 };
  input.velocity = { x: 0, y: 0 };
  input.sprint = false;
}

export function addLook(input, x, y) {
  input.look.x = clamp(input.look.x + clamp(x, -120, 120), -240, 240);
  input.look.y = clamp(input.look.y + clamp(y, -120, 120), -240, 240);
}

/** Consume transient events once per frame; retain held input across frames. */
export function consumeInput(input, delta, settings) {
  const has = (...codes) => codes.some((code) => input.keys.has(code));
  let x = input.stick.x + Number(has('KeyD', 'ArrowRight')) - Number(has('KeyA', 'ArrowLeft'));
  let y = input.stick.y + Number(has('KeyW', 'ArrowUp')) - Number(has('KeyS', 'ArrowDown'));
  const length = Math.max(1, Math.hypot(x, y));
  x /= length;
  y /= length;
  const t = 1 - Math.exp(-frameDelta(delta) * (x || y ? 12 : 20));
  input.velocity.x += (x - input.velocity.x) * t;
  input.velocity.y += (y - input.velocity.y) * t;
  const config = normalizeLookSettings(settings);
  const intent = {
    move: { ...input.velocity },
    sprint: input.sprint || has('ShiftLeft', 'ShiftRight'),
    look: {
      yaw: -pointerDeltaToRadians(input.look.x, config.sensitivity),
      pitch: pointerDeltaToRadians(input.look.y, config.sensitivity) * (config.invertY ? 1 : -1),
    },
    actions: new Set(input.actions),
  };
  input.look.x = 0;
  input.look.y = 0;
  input.actions.clear();
  return intent;
}

export function smoothLook(current, target, delta, smoothing) {
  const t = smoothing === 0 ? 1 : 1 - Math.exp(-frameDelta(delta) * (140 - 131 * smoothing));
  return {
    yaw: current.yaw + (target.yaw - current.yaw) * t,
    pitch: current.pitch + (target.pitch - current.pitch) * t,
  };
}
